import { Box, Flex, Text, Spinner } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { FaCheck } from 'react-icons/fa';
import { Icon } from '@chakra-ui/react';

export interface AgenticStep {
	label: string;
	description?: string;
}

interface AgenticStepLoaderProps {
	title: string;
	subtitle?: string;
	steps: AgenticStep[];
	currentStep: number;
}

const PARTBAY_BLUE = '#0071E3';
const PARTBAY_BLUE_LIGHT = '#E6F1FD';
const COMPLETED_GREEN = '#38A169';
const PENDING_GRAY = '#CBD5E0';

const pulseRing = keyframes`
	0% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
	50% { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
	100% { transform: translate(-50%, -50%) scale(1); opacity: 0; }
`;

const fadeIn = keyframes`
	from { opacity: 0; transform: translateY(4px); }
	to { opacity: 1; transform: translateY(0); }
`;

export const AgenticStepLoader = ({
	title,
	subtitle,
	steps,
	currentStep,
}: AgenticStepLoaderProps) => {
	return (
		<Flex
			direction="column"
			align="center"
			justify="center"
			width="100%"
			minH="320px"
			py={10}
			px={4}
		>
			{/* Header */}
			<Text
				fontSize="lg"
				fontWeight="bold"
				color="gray.800"
				mb={1}
				textAlign="center"
			>
				{title}
			</Text>
			{subtitle && (
				<Text
					fontSize="sm"
					color="gray.500"
					mb={8}
					textAlign="center"
				>
					{subtitle}
				</Text>
			)}

			{/* Timeline */}
			<Box position="relative" width="fit-content" mx="auto">
				{steps.map((step, index) => {
					const isCompleted = index < currentStep;
					const isActive = index === currentStep;
					const isPending = index > currentStep;

					return (
						<Flex
							key={index}
							align="flex-start"
							position="relative"
							animation={`${fadeIn} 0.3s ease-out`}
						>
						{/* Circle + line column */}
						<Box
							w="28px"
							mr={4}
							flexShrink={0}
							position="relative"
							alignSelf="stretch"
						>
							{/* Connecting line — absolutely positioned behind the circle */}
							{index < steps.length - 1 && (
								<Box
									position="absolute"
									left="50%"
									top="28px"
									bottom="0"
									transform="translateX(-50%)"
										w="2.5px"
										bg={
											isCompleted
												? COMPLETED_GREEN
												: PENDING_GRAY
										}
										bgGradient={
											isActive
												? `linear(to-b, ${PARTBAY_BLUE}, ${PENDING_GRAY})`
												: undefined
										}
										transition="background 0.3s ease"
										borderRadius="full"
									/>
								)}

								{/* Circle indicator */}
								<Box position="relative" zIndex={1}>
									{/* Pulse ring for active step */}
									{isActive && (
										<Box
											position="absolute"
											top="50%"
											left="50%"
											transform="translate(-50%, -50%)"
											w="36px"
											h="36px"
											borderRadius="full"
											bg={PARTBAY_BLUE}
											opacity={0.2}
											animation={`${pulseRing} 2s ease-in-out infinite`}
										/>
									)}

									<Flex
										w="28px"
										h="28px"
										borderRadius="full"
										align="center"
										justify="center"
										bg={
											isCompleted
												? COMPLETED_GREEN
												: isActive
													? PARTBAY_BLUE
													: 'white'
										}
										border="2.5px solid"
										borderColor={
											isCompleted
												? COMPLETED_GREEN
												: isActive
													? PARTBAY_BLUE
													: PENDING_GRAY
										}
										transition="all 0.3s ease"
										boxShadow={
											isActive
												? `0 0 0 4px ${PARTBAY_BLUE_LIGHT}`
												: 'none'
										}
									>
										{isCompleted && (
											<Icon
												as={FaCheck as React.ElementType}
												color="white"
												boxSize="12px"
											/>
										)}
										{isActive && (
											<Spinner
												size="xs"
												color="white"
												thickness="2px"
												speed="0.8s"
											/>
										)}
										{isPending && (
											<Box
												w="8px"
												h="8px"
												borderRadius="full"
												bg={PENDING_GRAY}
											/>
										)}
									</Flex>
								</Box>
							</Box>

							{/* Step text content */}
							<Box
								pt="3px"
								pb={index < steps.length - 1 ? 6 : 0}
							>
								<Text
									fontSize="sm"
									fontWeight={
										isActive
											? 'semibold'
											: isCompleted
												? 'medium'
												: 'normal'
									}
									color={
										isCompleted
											? 'gray.600'
											: isActive
												? 'gray.800'
												: 'gray.400'
									}
									transition="all 0.3s ease"
								>
									{step.label}
								</Text>
								{step.description && (
									<Text
										fontSize="xs"
										color={
											isActive
												? 'gray.500'
												: 'gray.400'
										}
										mt={0.5}
										transition="all 0.3s ease"
									>
										{step.description}
									</Text>
								)}
							</Box>
						</Flex>
					);
				})}
			</Box>
		</Flex>
	);
};
